import { useCallback } from 'react';
import type { ReactNode, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@oneflowab/pomes';
import type { MessageTranslator } from '@oneflowab/pomes';
import { get, noop } from 'lodash';

import ModalForm from '@app/hocs/modal-form';
import agreementsReducer from '@app/reducers/entities/agreements';
import { isTemplate } from '@flow/libs/agreement/states';
import { redirectToDocumentOnDocumentCreate } from '@flow/libs/agreement/navigation-helpers';

import Button from '@app/components/button';
import { CancelButton } from '@app/components/buttons/cancel';
import { ContractNameField, TemplateNameField } from '@app/components/fields';
import { T } from '@app/components/translation';

import style from './copy-contract.module.scss';

type FormValues = {
  name: string;
};

type FormProps = {
  initialValues: FormValues;
};

export type Props = {
  agreement: Oneflow.Agreement;
  amplitudeScope: string;
  isCreatingTemplate: boolean;
  message: MessageTranslator;
  children: ((onClick: Function) => ReactNode) | ReactNode;
};

export const CopyContractComponent = ({
  agreement,
  amplitudeScope,
  isCreatingTemplate,
  message,
  children,
}: Props) => {
  const dispatch = useDispatch();
  const formState = useSelector(agreementsReducer.getCreateSelector);
  const getDefaultContractName = () => {
    if (!agreement.name) {
      return message({
        id: 'Untitled document - copy',
        comment: 'The message used as the default value for contract name when copying contracts.',
      });
    }

    if (isCreatingTemplate) {
      return agreement.name;
    }

    return message({
      id: '{name} – copy',
      values: {
        name: agreement.name,
      },
      comment: 'The message used as the default value for contract name when copying contract.',
    });
  };

  const getTitle = () => {
    if (isCreatingTemplate) {
      return (
        <T
          _str="Create template"
          _comment="Modal title for creating a template from contract."
        />
      );
    }

    return (
      <T
        _str="Duplicate"
        _comment="Modal title for copying a contract or a template."
      />
    );
  };

  const renderNameField = () => {
    if (isTemplate(agreement) || isCreatingTemplate) {
      return <TemplateNameField autoFocus />;
    }

    return <ContractNameField autoFocus />;
  };

  const copyContract = useCallback(
    (name: string) => {
      dispatch(
        agreementsReducer.createAgreement({
          data: {
            sourceId: agreement.id,
            name,
            isTemplate: isCreatingTemplate || isTemplate(agreement),
            amplitudeScope,
          },
          pipe: {
            onSuccess: (data) => {
              const { agreements } = data.entities;
              const newAgreement = agreements[data.result];

              if (isTemplate(newAgreement)) {
                return redirectToDocumentOnDocumentCreate(data, true);
              }

              return redirectToDocumentOnDocumentCreate(data);
            },
          },
        }),
      );
    },
    [
      dispatch,
      agreement,
      isCreatingTemplate,
      amplitudeScope,
    ],
  );

  const resetFormState = useCallback(() => {
    dispatch(agreementsReducer.createAgreementReset());
  }, [
    dispatch,
  ]);

  const getActions = useCallback(
    ({ formProps, closeConfirmation }: { formProps: FormProps; closeConfirmation: () => void }) => {
      const name = get(formProps, 'values.name');
      return (
        <div className={style.ActionButtonContainer}>
          <CancelButton onClick={closeConfirmation} />
          <Button
            kind="primary"
            disabled={formState.loading}
            isLoading={formState.loading}
            onClick={() => copyContract(name)}
          >
            <T
              _str="Save"
              _comment="Text for button to save the copy of the contract."
            />
          </Button>
        </div>
      );
    },
    [
      formState.loading,
      copyContract,
    ],
  );

  return (
    <ModalForm
      title={getTitle()}
      body={<div>{renderNameField()}</div>}
      onSubmit={noop}
      resetFormState={resetFormState}
      formState={formState}
      actions={getActions}
      initialValues={{
        name: getDefaultContractName(),
      }}
      allowPristine
      modalKey="copy contract/template modal"
    >
      {children}
    </ModalForm>
  );
};

type LocalizedProps = Omit<Props, 'message'>;
export const CopyContractModal = localize<Props>(
  CopyContractComponent,
) as unknown as FC<LocalizedProps>;
